import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { fetchConfig, isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number | null} */
  #bestsellerAutoScrollTimer = null;

  /** @type {HTMLElement | null} */
  #bestsellerTrack = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#initBestsellerAutoScroll);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#destroyBestsellerAutoScroll);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener('submit', this.#handleUpsellSubmit);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#initBestsellerAutoScroll);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#destroyBestsellerAutoScroll);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener('submit', this.#handleUpsellSubmit);
    this.#destroyBestsellerAutoScroll();
    this.#historyAbortController?.abort();
  }

  /** @param {SubmitEvent} event */
  #handleUpsellSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.classList.contains('cart-drawer-bestsellers__form')) return;

    event.preventDefault();

    const submitButton = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'));
    submitButton?.setAttribute('aria-busy', 'true');
    if (submitButton) submitButton.disabled = true;

    const formData = new FormData(form);
    const variantId = formData.get('id')?.toString();
    const quantity = Number(formData.get('quantity')) || 1;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length) {
      formData.set('sections', cartItemComponentsSectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response?.status) return;
        if (!variantId) return;

        form.dispatchEvent(
          new CartAddEvent({}, variantId, {
            source: 'cart-drawer-bestsellers',
            itemCount: quantity,
            sections: response.sections,
          })
        );
      })
      .catch((error) => console.error(error))
      .finally(() => {
        submitButton?.removeAttribute('aria-busy');
        if (submitButton) submitButton.disabled = false;
      });
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #initBestsellerAutoScroll = () => {
    this.#destroyBestsellerAutoScroll();

    const track = /** @type {HTMLElement | null} */ (this.querySelector('[data-bestsellers-track]'));
    if (!track) return;

    this.#bestsellerTrack = track;

    const autoScrollEnabled = track.dataset.autoscrollEnabled === 'true';
    const intervalMs = Number(track.dataset.autoscrollIntervalMs) || 3000;
    if (!autoScrollEnabled || track.scrollWidth <= track.clientWidth) return;

    this.#bestsellerAutoScrollTimer = window.setInterval(() => {
      const firstItem = /** @type {HTMLElement | null} */ (track.querySelector('.cart-drawer-bestsellers__item'));
      if (!firstItem) return;

      const gap = Number.parseFloat(window.getComputedStyle(track).columnGap || '0') || 0;
      const step = firstItem.getBoundingClientRect().width + gap;
      const maxScrollLeft = Math.max(track.scrollWidth - track.clientWidth, 0);
      const nextScrollLeft = track.scrollLeft + step;

      if (nextScrollLeft >= maxScrollLeft - 2) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }

      track.scrollTo({ left: nextScrollLeft, behavior: 'smooth' });
    }, Math.max(intervalMs, 1000));
  };

  #destroyBestsellerAutoScroll = () => {
    if (this.#bestsellerAutoScrollTimer !== null) {
      window.clearInterval(this.#bestsellerAutoScrollTimer);
      this.#bestsellerAutoScrollTimer = null;
    }
    this.#bestsellerTrack = null;
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  /** Called after cart morph (hydration) so summary sticky + layout match new markup */
  updateSummaryStickyState() {
    this.#updateStickyState();
    this.#initBestsellerAutoScroll();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
